import type { World } from './world';
import type { AnyUnit, Projectile, Zone } from './types';
import { dealDamage, heal } from './combat';
import { applyStatus } from './status';
import { runAction } from './actions';
import { wrapAngle } from '../math';

export function stepProjectiles(world: World): void {
  const dt = world.dt;
  for (const [id, p] of world.projectiles) {
    const owner = world.unit(p.owner);
    if (p.orbit) {
      if (!owner || owner.dead) {
        world.projectiles.delete(id);
        world.events.push({ t: 'projEnd', id, x: p.x, y: p.y });
        continue;
      }
      p.orbit.angle += p.orbit.angVel * dt;
      const nx = owner.x + Math.cos(p.orbit.angle) * p.orbit.radius;
      const ny = owner.y + Math.sin(p.orbit.angle) * p.orbit.radius;
      p.vx = (nx - p.x) / dt;
      p.vy = (ny - p.y) / dt;
      p.x = nx;
      p.y = ny;
      // orbiting blades can hit the same target again after a while
      if (world.tick % 8 === 0) p.hits.clear();
    } else {
      if (p.homing > 0) {
        let target = p.targetId ? world.unit(p.targetId) : undefined;
        if (!target || target.dead || p.hits.has(target.id)) {
          target = world.nearestEnemy(p.x, p.y, 320, p.team);
          p.targetId = target ? target.id : 0;
        }
        if (target) {
          const want = Math.atan2(target.y - p.y, target.x - p.x);
          const cur = Math.atan2(p.vy, p.vx);
          const speed = Math.hypot(p.vx, p.vy);
          const turn = Math.max(-p.homing * dt, Math.min(p.homing * dt, wrapAngle(want - cur)));
          const na = cur + turn;
          p.vx = Math.cos(na) * speed;
          p.vy = Math.sin(na) * speed;
        }
      }
      if (p.returning && !p.returned && world.tick >= p.until - 1) {
        p.returned = true;
        p.hits.clear();
        p.until = world.tick + (world.tick - p.spawnTick) + 2;
      }
      if (p.returned && owner) {
        const speed = Math.hypot(p.vx, p.vy);
        const a = Math.atan2(owner.y - p.y, owner.x - p.x);
        p.vx = Math.cos(a) * speed;
        p.vy = Math.sin(a) * speed;
        if (Math.hypot(owner.x - p.x, owner.y - p.y) < 20) {
          world.projectiles.delete(id);
          world.events.push({ t: 'projEnd', id, x: p.x, y: p.y });
          continue;
        }
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      if (world.map.isSolidAt(p.x, p.y)) {
        if (p.bounce > 0) {
          p.bounce--;
          // reflect on the axis we crossed
          if (world.map.isSolidAt(p.x - p.vx * dt, p.y)) p.vy = -p.vy;
          else p.vx = -p.vx;
          p.x -= p.vx * dt * 0.5;
          p.y -= p.vy * dt * 0.5;
        } else {
          expire(world, p, owner);
          continue;
        }
      }
    }
    if (world.tick >= p.until) {
      expire(world, p, owner);
      continue;
    }
    // collisions
    const targets = world.enemiesNear(p.x, p.y, p.r + 24, p.team);
    for (const e of targets) {
      if (p.hits.has(e.id)) continue;
      const dx = e.x - p.x;
      const dy = e.y - p.y;
      const rr = e.r + p.r;
      if (dx * dx + dy * dy > rr * rr) continue;
      p.hits.add(e.id);
      hitTarget(world, p, e, owner);
      if (p.pierce <= 0 && !p.orbit) {
        world.projectiles.delete(id);
        world.events.push({ t: 'projEnd', id, x: p.x, y: p.y });
        break;
      }
      p.pierce--;
    }
  }
}

function hitTarget(world: World, p: Projectile, e: AnyUnit, owner: AnyUnit | undefined): void {
  const crit = world.rng.chance(p.critChance);
  const dmg = p.damage * (crit ? p.critMult : 1);
  dealDamage(world, owner ?? null, e, dmg, p.tags, { crit, fromSkill: p.fromSkill });
  if (!e.dead) {
    if (p.status) for (const s of p.status) applyStatus(world, e, s, owner ?? null, p.power);
    if (p.knockback) {
      const a = Math.atan2(p.vy, p.vx);
      e.knockX += Math.cos(a) * p.knockback;
      e.knockY += Math.sin(a) * p.knockback;
    }
  }
  if (p.onHit && owner && !owner.dead) {
    runAction(world, p.onHit, { caster: owner, x: p.x, y: p.y, aimX: e.x, aimY: e.y, target: e, power: p.power, mul: p.damage / Math.max(1, p.power) / Math.max(0.01, owner.stats.damage), fromSkill: p.fromSkill, depth: 1 });
  }
}

function expire(world: World, p: Projectile, owner: AnyUnit | undefined): void {
  world.projectiles.delete(p.id);
  world.events.push({ t: 'projEnd', id: p.id, x: p.x, y: p.y });
  if (p.onExpire && owner && !owner.dead) {
    runAction(world, p.onExpire, { caster: owner, x: p.x, y: p.y, aimX: p.x, aimY: p.y, power: p.power, mul: 1, fromSkill: p.fromSkill, depth: 1 });
  }
}

export function stepZones(world: World): void {
  const dt = world.dt;
  for (const [id, z] of world.zones) {
    if (world.tick >= z.until) {
      world.zones.delete(id);
      continue;
    }
    const owner = world.unit(z.owner) ?? null;
    const enemies = world.enemiesNear(z.x, z.y, z.r, z.team);
    if (z.pull) {
      for (const e of enemies) {
        const a = Math.atan2(z.y - e.y, z.x - e.x);
        e.knockX += Math.cos(a) * z.pull * dt * 2;
        e.knockY += Math.sin(a) * z.pull * dt * 2;
      }
    }
    if (world.tick >= z.nextTick) {
      z.nextTick = world.tick + z.tickEvery;
      for (const e of enemies) {
        if (z.damage > 0) dealDamage(world, owner, e, z.damage, z.tags, { fromSkill: z.visual });
        if (!e.dead && z.status) for (const s of z.status) applyStatus(world, e, s, owner, z.power);
        if (z.knockback && !e.dead) {
          const a = Math.atan2(e.y - z.y, e.x - z.x);
          e.knockX += Math.cos(a) * z.knockback;
          e.knockY += Math.sin(a) * z.knockback;
        }
      }
      if (z.healAllies > 0) {
        for (const u of world.alliesNear(z.x, z.y, z.r, z.team)) heal(world, u, z.healAllies / (20 / z.tickEvery), owner);
      }
    }
  }
}

export type { Zone };
